import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCopy, Loader2, PackageCheck, Plus, Search, Ticket } from "lucide-react";
import { api, apiErrorMessage } from "../../../lib/api";
import {
  AREA, GROUPS, IMPACT, KIND, KIND_ORDER, PRIORITY, REPORTER, REPORTER_ORDER, STATUS, formatMinutes, reporterWhere,
  ticketLabel,
} from "../../../lib/tickets";
import ReportDialog from "./ReportDialog";
import TicketDrawer from "./TicketDrawer";
import DeliverDialog from "./DeliverDialog";
import TimeSummary from "./TimeSummary";
import useDevCopy from "./useDevCopy";

/**
 * The tickets (14.10.2026): what the team reports about the platform, in one
 * list instead of a chat. Owner and admin FPED read everybody's ("Toate");
 * everybody else reads their own. The owner alone sees the time column and
 * which tickets are still unread.
 */
export default function TicketsTab({ readsAll = false, isOwner = false, me = "", onChanged }) {
  const [view, setView] = useState(readsAll ? "all" : "mine");
  const [group, setGroup] = useState("open");
  const [kind, setKind] = useState("");
  const [reporter, setReporter] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  // `?t=<id>` opens a ticket: the link in every ticket email.
  const [params, setParams] = useSearchParams();
  const [drawerId, setDrawerIdState] = useState(params.get("t"));
  const setDrawerId = (id) => {
    setDrawerIdState(id);
    const next = new URLSearchParams(params);
    if (id) next.set("t", id); else next.delete("t");
    setParams(next, { replace: true });
  };
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState([]);
  const [delivering, setDelivering] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [copyForDevelopment, copyFallback] = useDevCopy();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { view };
      if (group) params.group = group;
      if (kind) params.kind = kind;
      if (reporter) params.reporter = reporter;
      if (query) params.q = query;
      const r = await api.get("/admin/tickets", { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setCounts(r.data.counts || {});
    } catch (err) {
      toast.error(apiErrorMessage(err, "Tichetele nu au putut fi încărcate."));
    } finally {
      setLoading(false);
    }
  }, [view, group, kind, reporter, query]);

  useEffect(() => { load(); }, [load]);
  // A new filter shows other tickets: what was picked from the old list goes.
  useEffect(() => { setPicked([]); }, [view, group, kind, reporter, query]);
  // Searched once the typing pauses, not at every key.
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const changed = () => { load(); setRefreshKey((k) => k + 1); onChanged?.(); };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const pickedTickets = items.filter((t) => picked.includes(t.id));

  return (
    <div data-testid="tickets-tab">
      {isOwner && <TimeSummary refreshKey={refreshKey} />}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {readsAll && (
            <div className="inline-flex rounded-full border border-[#E5E7EB] bg-white p-0.5 mr-1">
              {[["all", "Toate"], ["mine", "Ale mele"]].map(([key, label]) => (
                <button key={key} onClick={() => setView(key)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold ${view === key ? "bg-[#965EA7] text-white" : "text-[#373A45] hover:bg-[#F5F6F6]"}`}
                  data-testid={`tickets-view-${key}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {GROUPS.map((g) => (
            <FilterChip key={g.key || "all"} active={group === g.key} onClick={() => setGroup(g.key)}
              label={g.label} count={counts[g.count || g.key]} testid={`tickets-group-${g.key || "all"}`} />
          ))}
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            className="rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs text-[#373A45]"
            data-testid="tickets-kind-filter" aria-label="Tip">
            <option value="">Orice tip</option>
            {KIND_ORDER.map((key) => <option key={key} value={key}>{KIND[key].label}</option>)}
          </select>
          {readsAll && (
            <select value={reporter} onChange={(e) => setReporter(e.target.value)}
              className="rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs text-[#373A45]"
              data-testid="tickets-reporter-filter" aria-label="Raportat de">
              <option value="">Raportat de oricine</option>
              {REPORTER_ORDER.map((key) => <option key={key} value={key}>{REPORTER[key].label}</option>)}
            </select>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Caută T-12 sau un cuvânt din titlu…"
              className="pl-8 pr-3 py-2 text-sm bg-white border border-[#E5E7EB] rounded-full focus:ring-2 focus:ring-[#965EA7] outline-none w-64"
              data-testid="tickets-search" />
          </div>
          <button onClick={() => setCreating(true)}
            className="rounded-full px-3 py-2 text-sm font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] inline-flex items-center gap-1.5"
            data-testid="tickets-new">
            <Plus size={14} /> Tichet nou
          </button>
        </div>
      </div>

      {readsAll && picked.length > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap rounded-xl bg-[#373A45] text-white px-4 py-2 text-sm" data-testid="tickets-picked-bar">
          <span className="font-semibold">{picked.length === 1 ? "Un tichet ales" : `${picked.length} tichete alese`}</span>
          <span className="flex-1" />
          <button onClick={() => copyForDevelopment(picked)}
            className="rounded-full px-3 py-1 text-xs font-semibold bg-white text-[#373A45] hover:bg-[#F5F6F6] inline-flex items-center gap-1.5"
            data-testid="tickets-copy-dev">
            <ClipboardCopy size={12} /> Copiază pentru dezvoltare
          </button>
          {isOwner && (
            <button onClick={() => setDelivering(true)}
              className="rounded-full px-3 py-1 text-xs font-semibold bg-[#965EA7] text-white hover:bg-[#7A4986] inline-flex items-center gap-1.5"
              data-testid="tickets-deliver">
              <PackageCheck size={12} /> Marchează livrate
            </button>
          )}
          <button onClick={() => setPicked([])} className="text-xs underline opacity-80 hover:opacity-100">Renunță</button>
        </div>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-[#6B7280] inline-flex items-center gap-2 w-full justify-center">
            <Loader2 className="animate-spin" size={16} /> Se încarcă…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-[#6B7280]" data-testid="tickets-empty">
            <Ticket size={36} className="mx-auto mb-3 text-[#965EA7]/30" />
            {counts.all || reporter ? "Niciun tichet pentru acest filtru." : "Încă nu e niciun tichet."}
            <div className="mt-1 text-xs">
              Ai găsit ceva ce nu merge sau ai o idee? Apasă <strong>Tichet nou</strong>, sau <strong>Raportează</strong> sus, din orice pagină.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F5F6F6] text-[#6B7280] text-xs uppercase tracking-widest">
              <tr>
                {readsAll && <th className="w-8 px-3 py-3" aria-label="Alege" />}
                <Th>Nr.</Th>
                <Th>Titlu</Th>
                <Th>Tip</Th>
                <Th>Stare</Th>
                <Th>Prioritate</Th>
                <Th>Impact</Th>
                <Th>Zona</Th>
                {(readsAll && view === "all") && <Th>Raportat de</Th>}
                <Th>Creat</Th>
                {isOwner && <Th>Timp</Th>}
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} onClick={() => setDrawerId(t.id)}
                  className="border-t border-[#E5E7EB] cursor-pointer hover:bg-[#F5F6F6]/60"
                  data-testid={`ticket-row-${t.number}`}>
                  {readsAll && (
                    <td className="px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={picked.includes(t.id)} onChange={() => toggle(t.id)}
                        className="h-3.5 w-3.5 accent-[#965EA7]" aria-label={`Alege ${ticketLabel(t)}`} />
                    </td>
                  )}
                  <Td className="font-mono font-semibold text-[#7A4986] whitespace-nowrap">
                    {isOwner && t.unread && (
                      <span className="inline-block h-2 w-2 rounded-full bg-[#965EA7] mr-1.5 align-middle" title="Necitit" />
                    )}
                    {ticketLabel(t)}
                  </Td>
                  <Td className={`min-w-[16rem] max-w-[28rem] ${isOwner && t.unread ? "font-semibold text-[#373A45]" : "text-[#373A45]"}`}>{t.title}</Td>
                  <Td><Pill meta={KIND[t.kind]} short /></Td>
                  <Td><Pill meta={STATUS[t.status]} /></Td>
                  <Td>{t.priority ? <Pill meta={PRIORITY[t.priority]} /> : <span className="text-[#9CA3AF]">—</span>}</Td>
                  <Td className={`text-xs whitespace-nowrap ${IMPACT[t.impact]?.cls || ""}`}>{IMPACT[t.impact]?.short || IMPACT[t.impact]?.label}</Td>
                  <Td className="text-xs">{AREA[t.area] || "—"}</Td>
                  {(readsAll && view === "all") && (
                    <Td className="text-xs">
                      {t.reporter?.name || t.reporter?.email}
                      {t.reporter?.kind && t.reporter.kind !== "team" && (
                        <span className={`ml-1.5 rounded-full px-1.5 py-px text-[10px] font-semibold ${REPORTER[t.reporter.kind]?.cls || ""}`}
                          title={reporterWhere(t.reporter)}>
                          {REPORTER[t.reporter.kind]?.label}
                        </span>
                      )}
                    </Td>
                  )}
                  <Td className="text-xs text-[#6B7280] whitespace-nowrap">{(t.created_at || "").slice(0, 10)}</Td>
                  {isOwner && (
                    <Td className="text-xs whitespace-nowrap tabular-nums">
                      {t.time_total_minutes ? formatMinutes(t.time_total_minutes) : <span className="text-[#9CA3AF]">—</span>}
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {total > items.length && (
          <div className="p-3 text-xs text-[#6B7280] text-center bg-[#F5F6F6]/40">Afișare {items.length} din {total}</div>
        )}
      </div>

      <ReportDialog open={creating} onClose={() => setCreating(false)} via="tab" onCreated={changed} />
      {drawerId && (
        <TicketDrawer ticketId={drawerId} isOwner={isOwner} readsAll={readsAll} me={me} onChanged={changed}
          onClose={() => setDrawerId(null)} />
      )}
      {copyFallback}
      <DeliverDialog open={delivering} labels={pickedTickets.map(ticketLabel)} onClose={() => setDelivering(false)}
        onDone={() => { setPicked([]); changed(); }} />
    </div>
  );
}

export function Pill({ meta, short = false }) {
  if (!meta) return <span className="text-[#9CA3AF]">—</span>;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest whitespace-nowrap ${meta.cls}`}>
      {short && meta.short ? meta.short : meta.label}
    </span>
  );
}

function FilterChip({ active, onClick, label, count, testid }) {
  const cls = active ? "bg-[#373A45] text-white border-[#373A45]" : "border border-[#E5E7EB] text-[#373A45] hover:bg-[#F5F6F6]";
  return (
    <button onClick={onClick} className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${cls}`} data-testid={testid}>
      {label}{typeof count === "number" && <span className="ml-1 opacity-70 tabular-nums">{count}</span>}
    </button>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 font-semibold">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
